/**
 * The area tree (Decision Compiler §11): `agentic/<domain>/<area[/<subarea>]/`.
 *
 * Every folder level is a node: questions live in its `_questions/`, items are
 * filed by their `subcategory`, and the founder controls each node with one
 * mode (absent = untouched). This module builds the console's portfolio model
 * from parsed questions + items + `_node.yml` modes.
 */

import type { Item } from "./item";
import type { Question } from "./questions";

export type NodeMode =
  | "explore"
  | "refine"
  | "consolidate"
  | "challenge"
  | "autopilot"
  | "hold"
  | "waived"
  | "custom";

/** The pickable steering modes (Custom has its own row with a free-text field). */
export const MODES: readonly NodeMode[] = [
  "explore",
  "refine",
  "consolidate",
  "challenge",
  "autopilot",
  "hold",
  "waived",
];

export const MODE_LABELS: Record<NodeMode, string> = {
  explore: "Explore",
  refine: "Refine",
  consolidate: "Wrap up",
  challenge: "Challenge",
  autopilot: "Answer for me",
  hold: "Pause",
  waived: "Never ask",
  custom: "Custom",
};

/** The default state (no `_node.yml` yet) — explained like every mode. */
export const DEFAULT_MODE_LABEL = "Standard";
export const DEFAULT_MODE_EXPLAINER =
  "The everyday setting: when a run is triggered for this area, it may ask if the agents find something real — you only steer when needed.";

export const MODE_EXPLAINERS: Record<NodeMode, string> = {
  explore:
    "First pass: the agents map what this area is, where its boundaries are, and whether it should be split. Afterwards the area returns to standard.",
  refine:
    "The agents build on your answers and ask the next, sharper questions. Runs until you pause it or nothing better can be asked.",
  consolidate:
    "Only the closing questions that finish this area are asked. Afterwards the area is ready to wrap up.",
  challenge:
    "Re-opens decided questions against new evidence — every challenge must cite its source. No limit; Pause switches it off.",
  autopilot:
    "Open questions are decided by recommendation; only high-impact questions still reach you.",
  hold: "Nothing is asked here until you reopen it.",
  waived:
    "Everything open counts as 'not relevant'. The area is considered done — no more questions, except from Challenge.",
  custom:
    "Your own instructions: write free text and the agents follow it for this area on every run (and for rework runs).",
};

/** Modes that keep the questions away from the founder (session assembly). */
export const QUIET_MODES: ReadonlySet<string> = new Set([
  "hold",
  "waived",
  "autopilot",
]);

/**
 * Effective mode for a node: its own `_node.yml`, else the nearest parent
 * folder's (up to the domain default), else "untouched" (= Standard).
 * Domain defaults live in `agentic/<domain>/_node.yml`.
 */
export function effectiveMode(
  modes: ReadonlyMap<string, NodeMode | "untouched">,
  node: string,
): NodeMode | "untouched" {
  const parts = node.split("/");
  if (parts.length < 2 || parts[0] !== "agentic") return "untouched";
  for (let i = parts.length; i >= 2; i--) {
    const m = modes.get(parts.slice(0, i).join("/"));
    if (m) return m;
  }
  return "untouched";
}

export function isNodeMode(value: string): value is NodeMode {
  return value === "custom" || (MODES as readonly string[]).includes(value);
}

/** The free-text `note:` of a custom-mode `_node.yml` ("" when absent). */
export function parseNodeNote(content: string): string {
  const m = /^note:\s*"?([^"\n]*?)"?\s*$/m.exec(content);
  return m ? m[1].trim() : "";
}

/** `agentic/<domain>` + area parts → node path. */
export function nodePath(domain: string, area: string): string {
  return area ? `agentic/${domain}/${area}` : `agentic/${domain}`;
}

export interface AreaStats {
  open: number;
  answered: number;
  delegated: number;
  waived: number;
  closed: number;
  superseded: number;
  total: number;
}

export function emptyStats(): AreaStats {
  return {
    open: 0,
    answered: 0,
    delegated: 0,
    waived: 0,
    closed: 0,
    superseded: 0,
    total: 0,
  };
}

/** Plain-language node state (mirrors `conductor.node_label`). */
export function nodeStatusLabel(
  stats: AreaStats,
  mode: NodeMode | "untouched",
): string {
  if (mode === "waived") return "never";
  if (mode === "hold") return "paused";
  if (stats.total === 0) return "untouched";
  if (stats.open === 0) return "closable";
  return `asking (${stats.open})`;
}

export interface AreaInfo {
  /** Node path, e.g. `agentic/legal/consent`. */
  path: string;
  domain: string;
  /** Node tail, e.g. `consent` or `api/auth`. */
  area: string;
  mode: NodeMode | "untouched";
  stats: AreaStats;
  questions: Question[];
  items: Item[];
}

export interface DomainInfo {
  domain: string;
  areas: AreaInfo[];
  open: number;
  total: number;
}

/** Read a `_node.yml` mode (tolerates comments/unknown values). */
export function parseNodeMode(yml: string): NodeMode | "untouched" {
  const m = /^\s*mode\s*:\s*([a-z]+)\s*$/m.exec(yml);
  if (m && isNodeMode(m[1])) return m[1];
  return "untouched";
}

/** Content written to `_node.yml` (mirrors `questions.write_mode`). */
export function nodeMetaYaml(mode: NodeMode, today: string, note = ""): string {
  const clean = note.replace(/\s+/g, " ").trim().replace(/"/g, "'");
  return (
    "# Area metadata (Decision Compiler). Edited by the console / conductor.\n" +
    `mode: ${mode}\n` +
    (clean ? `note: "${clean}"\n` : "") +
    `updated: ${today}\n`
  );
}

function areaKey(item: Item): { domain: string; area: string } {
  return {
    domain: item.domain,
    area: (item.subcategory ?? "").trim(),
  };
}

function newArea(domain: string, area: string, mode: NodeMode | "untouched"): AreaInfo {
  return {
    path: nodePath(domain, area),
    domain,
    area,
    mode,
    stats: emptyStats(),
    questions: [],
    items: [],
  };
}

/**
 * Build the portfolio tree: questions grouped by their node, items filed by
 * `subcategory` (or pulled in when linked from a question of that area).
 */
export function buildTree(
  questions: Question[],
  items: Item[],
  modes: ReadonlyMap<string, NodeMode | "untouched">,
): DomainInfo[] {
  const areas = new Map<string, AreaInfo>();
  const ensure = (domain: string, area: string): AreaInfo => {
    const path = nodePath(domain, area);
    let a = areas.get(path);
    if (!a) {
      a = newArea(domain, area, effectiveMode(modes, path));
      areas.set(path, a);
    }
    return a;
  };

  for (const q of questions) {
    const nodeParts = q.node.split("/");
    const domain = nodeParts[1] ?? "";
    const area = nodeParts.slice(2).join("/");
    const a = ensure(domain, area);
    a.questions.push(q);
    a.stats.total++;
    a.stats[q.status]++;
  }

  const byPath = new Map(items.map((i) => [i.path, i]));
  for (const item of items) {
    const { domain, area } = areaKey(item);
    ensure(domain, area === "" ? "" : area).items.push(item);
  }
  // Items linked by an area's questions show up in that area as well — an item
  // filed at domain level may belong to an area only through its links.
  const seenIn = new Map<string, Set<string>>();
  for (const area of areas.values()) {
    seenIn.set(area.path, new Set(area.items.map((i) => i.path)));
  }
  for (const q of questions) {
    const nodeParts = q.node.split("/");
    const domain = nodeParts[1] ?? "";
    const area = nodeParts.slice(2).join("/");
    const target = ensure(domain, area);
    const seen = seenIn.get(target.path) ?? new Set<string>();
    seenIn.set(target.path, seen);
    for (const link of q.links) {
      const item = byPath.get(link);
      if (item && !seen.has(item.path)) {
        target.items.push(item);
        seen.add(item.path);
      }
    }
  }

  const domains = new Map<string, DomainInfo>();
  for (const area of areas.values()) {
    let d = domains.get(area.domain);
    if (!d) {
      d = { domain: area.domain, areas: [], open: 0, total: 0 };
      domains.set(area.domain, d);
    }
    d.areas.push(area);
    d.open += area.stats.open;
    d.total += area.stats.total;
  }

  const out = [...domains.values()];
  for (const d of out) {
    d.areas.sort((a, b) => a.area.localeCompare(b.area));
  }
  out.sort((a, b) => a.domain.localeCompare(b.domain));
  return out;
}
